import React, { useEffect, useMemo, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Card, Skeleton } from "@/hq/HQPrimitives";
import { toast } from "sonner";
import {
  X, CheckCircle2, Clock, MousePointerClick, Mail, Zap,
  ExternalLink, Phone, Globe, User as UserIcon, AtSign,
  FileText, Eye, AlertTriangle,
} from "lucide-react";
import CopyButton from "@/hq/CopyButton";
import FollowUpsSection from "@/hq/campaigns/FollowUpsSection";
import FollowUpGeneratorModal from "@/hq/campaigns/FollowUpGeneratorModal";

/**
 * Phase 3I.4Q.2 + 3I.4R.2 — Pitch Detail modal.
 *
 * Renders the byte-exact rendered HTML that was archived at send time.
 * If historical content is unavailable AND the template type can be
 * identified, offers a clearly-labelled REFERENCE-ONLY preview based
 * on the current template. NEVER regenerates the record or claims a
 * reference render is the exact email that was sent.
 */

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

// Phase 3I.4Q.2 — use the canonical shared `asText` helper (see /app/frontend/src/hq/asText.js).
import { asText as sharedAsText } from "@/hq/asText";
const asText = sharedAsText;

// Best-effort URL normaliser — ensures a plain www.foo.com resolves as https://.
const hrefFor = (url) => {
  const u = asText(url);
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  return `https://${u.replace(/^\/+/, "")}`;
};

function Section({ title, testId, children }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">{title}</div>
      <div className="space-y-1 text-[13px]">{children}</div>
    </div>
  );
}

/**
 * Row primitive — left label, right value with optional inline icon
 * link (mailto:/tel:/https://) and small Copy button. Falls back to the
 * plain-text cell when no `value` is present.
 */
function Row({ k, v, testId, href, external = false, copy = true }) {
  const value = asText(v);
  return (
    <div
      className="flex justify-between gap-3 border-b border-dashed border-[#EAEAEA] py-1.5"
      data-testid={testId}
    >
      <div className="text-[#8A8A8A] text-[11px] uppercase tracking-[0.14em] font-bold shrink-0">{k}</div>
      <div className="text-right flex items-center gap-2 min-w-0">
        {value ? (
          <>
            {href ? (
              <a
                href={href}
                target={external ? "_blank" : undefined}
                rel={external ? "noopener noreferrer" : undefined}
                className="truncate text-[#0A0A0A] hover:text-[#FF6A00] hover:underline inline-flex items-center gap-1"
                data-testid={testId ? `${testId}-link` : undefined}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="truncate">{value}</span>
                {external && <ExternalLink size={11} className="shrink-0 opacity-60"/>}
              </a>
            ) : (
              <span className="truncate">{value}</span>
            )}
            {copy && <CopyButton value={value} testId={testId ? `${testId}-copy` : undefined}/>}
          </>
        ) : (
          <span className="text-[#8A8A8A]">—</span>
        )}
      </div>
    </div>
  );
}

export default function PitchDetailModal({ commId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historicalPreview, setHistoricalPreview] = useState(null); // {html, subject, disclaimer}
  const [historicalError, setHistoricalError] = useState(null);
  const [loadingHistorical, setLoadingHistorical] = useState(false);
  const [showFollowUpGen, setShowFollowUpGen] = useState(false);
  const [followUpRefreshKey, setFollowUpRefreshKey] = useState(0);
  const open = !!commId;

  useEffect(() => {
    if (!open) { setData(null); setHistoricalPreview(null); setHistoricalError(null); return; }
    setData(null); setHistoricalPreview(null); setHistoricalError(null);
    hqApi.get(`/control/pitches/${commId}`)
      .then((r) => setData(r.data))
      .catch(() => { toast.error("Failed to load pitch detail"); onClose?.(); });
  }, [commId]); // eslint-disable-line react-hooks/exhaustive-deps

  const clickEvents = useMemo(
    () => (data?.timeline || []).filter((a) => /click/i.test(a.event_type || "")),
    [data]
  );

  if (!open) return null;

  const markInterested = async () => {
    setBusy(true);
    try {
      await hqApi.post(`/control/communications/${commId}/interested`, { source: "admin" });
      toast.success("Marked Interested");
      // Refresh detail + parent list
      const r = await hqApi.get(`/control/pitches/${commId}`);
      setData(r.data);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark Interested");
    } finally { setBusy(false); }
  };

  const unmarkInterested = async () => {
    setBusy(true);
    try {
      await hqApi.delete(`/control/communications/${commId}/interested`);
      toast.success("Cleared Interested");
      const r = await hqApi.get(`/control/pitches/${commId}`);
      setData(r.data);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clear Interested");
    } finally { setBusy(false); }
  };

  const loadHistoricalPreview = async () => {
    setLoadingHistorical(true);
    setHistoricalError(null);
    try {
      const r = await hqApi.get(`/control/pitches/${commId}/historical-template-preview`);
      setHistoricalPreview(r.data);
    } catch (e) {
      setHistoricalError(e.response?.data?.detail || "Could not load reference preview.");
    } finally { setLoadingHistorical(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-2 md:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="hq-pitch-detail-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Pitch Detail</div>
            <h2 className="mt-1 font-black text-2xl tracking-[-0.02em]" data-testid="hq-pitch-detail-title">
              {data?.business?.name || "…"} · <span className="text-[#FF6A00]">{data?.pitch_type === "full" ? "Full Pitch" : (data?.pitch_type || "").toUpperCase()}</span>
            </h2>
          </div>
          <button data-testid="hq-pitch-detail-close" onClick={onClose} className="p-2 hover:bg-[#F6F5F2] rounded-full"><X size={20}/></button>
        </div>

        {data && (
          <div className="px-6 pt-4">
            <FollowUpsSection commId={commId}
                              refreshKey={followUpRefreshKey}
                              onGenerate={() => setShowFollowUpGen(true)}/>
          </div>
        )}

        {!data ? (
          <div className="p-6"><Skeleton h={280}/></div>
        ) : (
          <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card className="p-4 space-y-4">
              <Section title="Business" testId="hq-pitch-detail-business">
                <Row k="Name"        v={data.business.name}     testId="hq-pitch-detail-business-name"/>
                <Row k="Industry"    v={data.business.industry} copy={false}/>
                <Row k="Territory"   v={data.business.territory} copy={false}/>
                <Row k="Orange Score" v={data.business.orange_score ?? "—"} copy={false} testId="hq-pitch-detail-orange-score"/>
                <Row k="Website"     v={data.business.website}
                     href={hrefFor(data.business.website)} external
                     testId="hq-pitch-detail-website"/>
                <Row k="Status"      v={data.business.status} copy={false}/>
              </Section>
              <Section title="Contact" testId="hq-pitch-detail-contact">
                <Row k="Name"  v={data.contact.full_name} testId="hq-pitch-detail-contact-name"/>
                <Row k="Email" v={data.contact.email || data.pitch.recipient}
                     href={(data.contact.email || data.pitch.recipient) ? `mailto:${data.contact.email || data.pitch.recipient}` : null}
                     testId="hq-pitch-detail-contact-email"/>
                <Row k="Phone" v={data.contact.phone || data.business.phone}
                     href={(data.contact.phone || data.business.phone) ? `tel:${String(data.contact.phone || data.business.phone).replace(/\s+/g, "")}` : null}
                     testId="hq-pitch-detail-contact-phone"/>
              </Section>
              <Section title="Pitch" testId="hq-pitch-detail-pitch">
                <Row k="Subject"    v={data.pitch.subject}      testId="hq-pitch-detail-subject"/>
                <Row k="Sender"     v={data.pitch.sender}       copy={false}/>
                <Row k="Recipient"  v={data.pitch.recipient}
                     href={data.pitch.recipient ? `mailto:${data.pitch.recipient}` : null}
                     testId="hq-pitch-detail-recipient"/>
                <Row k="Created"    v={fmt(data.pitch.created_at)} copy={false}/>
                <Row k="Sent"       v={fmt(data.pitch.sent_at)} copy={false}/>
                <Row k="Status"     v={data.pitch.status}       copy={false}/>
                <Row k="Delivery"   v={data.pitch.delivery_status} copy={false}/>
                <Row k="Campaign"   v={data.campaign || "Direct"} copy={false}/>
              </Section>
            </Card>

            <Card className="p-4 space-y-4">
              <Section title="Engagement" testId="hq-pitch-detail-engagement">
                <Row k="State" v={data.engagement.state} copy={false} testId="hq-pitch-detail-state"/>
                <Row k="Open count" v={data.engagement.open_count} copy={false} testId="hq-pitch-detail-open-count"/>
                <Row k="First opened" v={fmt(data.engagement.first_opened_at)} copy={false}/>
                <Row k="Last opened" v={fmt(data.engagement.last_opened_at)} copy={false}/>
                <Row k="Click count" v={data.engagement.click_count} copy={false} testId="hq-pitch-detail-click-count"/>
                <Row k="First clicked" v={fmt(data.engagement.first_clicked_at)} copy={false}/>
                <Row k="Last clicked" v={fmt(data.engagement.last_clicked_at)} copy={false}/>
                <Row k="Interested" v={data.engagement.interested_at ? fmt(data.engagement.interested_at) : "No"} copy={false} testId="hq-pitch-detail-interested"/>
                {data.engagement.interested_source && (
                  <Row k="Interest source" v={data.engagement.interested_source} copy={false}/>
                )}
              </Section>

              <div className="flex flex-wrap gap-2 pt-1">
                {!data.engagement.interested_at ? (
                  <button
                    data-testid="hq-pitch-detail-mark-interested"
                    onClick={markInterested}
                    disabled={busy || data.pitch.status !== "sent"}
                    className="px-3 py-2 rounded-full bg-[#0F7B3E] text-white text-[11px] font-black tracking-[0.16em] uppercase disabled:opacity-40"
                  >
                    <CheckCircle2 size={13} className="inline mr-1"/> Mark Interested
                  </button>
                ) : (
                  <button
                    data-testid="hq-pitch-detail-clear-interested"
                    onClick={unmarkInterested}
                    disabled={busy}
                    className="px-3 py-2 rounded-full bg-white border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase text-[#4A4A4A] disabled:opacity-40"
                  >Clear Interested</button>
                )}
              </div>

              <Section title="Link activity" testId="hq-pitch-detail-clicks">
                {clickEvents.length === 0 ? (
                  <div className="text-[#8A8A8A] text-[12px]">No click events yet.</div>
                ) : (
                  <ul className="space-y-1">
                    {clickEvents.map((a) => (
                      <li key={a.id} className="flex items-center gap-2 text-[12px]" data-testid={`hq-pitch-detail-click-${a.id}`}>
                        <MousePointerClick size={12} className="text-[#FF6A00]"/>
                        <span className="font-mono truncate">{asText((a.metadata || {}).destination_url) || asText(a.description)}</span>
                        <span className="ml-auto text-[#8A8A8A]">{fmt(a.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="Timeline" testId="hq-pitch-detail-timeline">
                {(data.timeline || []).length === 0 ? (
                  <div className="text-[#8A8A8A] text-[12px]">No activity recorded.</div>
                ) : (
                  <ol className="space-y-2 relative pl-4 border-l border-[#EAEAEA]">
                    {data.timeline.map((a) => (
                      <li key={a.id} className="text-[12px]" data-testid={`hq-pitch-detail-timeline-${a.id}`}>
                        <div className="absolute -ml-[1.35rem] w-2 h-2 rounded-full bg-[#FF6A00]"/>
                        <div className="font-semibold">{asText(a.description) || asText(a.event_type)}</div>
                        <div className="text-[10px] text-[#8A8A8A] uppercase tracking-[0.14em]">
                          {asText(a.event_type)} · <Clock size={9} className="inline"/> {fmt(a.created_at)}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </Section>
            </Card>

            <Card className="p-4 space-y-3 lg:col-span-1">
              {data.rendered.html_available ? (
                <>
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-[#0F7B3E]"/>
                    <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#0F7B3E]"
                         data-testid="hq-pitch-detail-exact-sent-label">
                      Exact sent email
                    </div>
                  </div>
                  <div className="text-[11px] text-[#0F7B3E] font-bold" data-testid="hq-pitch-detail-html-available">
                    <Zap size={11} className="inline mr-1"/>
                    Byte-exact archive from {fmt(data.rendered.rendered_at)}
                  </div>
                  <iframe
                    title={`Pitch ${data.id}`}
                    srcDoc={data.rendered.html}
                    sandbox=""
                    data-testid="hq-pitch-detail-iframe"
                    className="w-full h-[520px] rounded-xl border border-[#EAEAEA] bg-white"
                  />
                </>
              ) : historicalPreview ? (
                <>
                  <div className="flex items-center gap-2">
                    <Eye size={14} className="text-[#8A2E00]"/>
                    <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#8A2E00]"
                         data-testid="hq-pitch-detail-historical-label">
                      Historical template preview
                    </div>
                  </div>
                  <div className="p-3 rounded-xl bg-[#FFF7EE] border border-[#FCE1C7] text-[12px]"
                       data-testid="hq-pitch-detail-historical-disclaimer">
                    <div className="flex items-start gap-2 text-[#8A2E00]">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0"/>
                      <p>{historicalPreview.disclaimer}</p>
                    </div>
                  </div>
                  <iframe
                    title={`Historical template preview ${data.id}`}
                    srcDoc={historicalPreview.html}
                    sandbox=""
                    data-testid="hq-pitch-detail-historical-iframe"
                    className="w-full h-[520px] rounded-xl border border-[#EAEAEA] bg-white"
                  />
                  <button
                    onClick={() => setHistoricalPreview(null)}
                    data-testid="hq-pitch-detail-historical-close"
                    className="text-[10px] tracking-[0.16em] uppercase font-black text-[#8A8A8A] hover:text-[#0A0A0A]"
                  >← Back</button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-[#FF6A00]"/>
                    <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Rendered content</div>
                  </div>
                  <div className="p-4 rounded-xl bg-[#FFF7EE] border border-[#FCE1C7] text-[13px]" data-testid="hq-pitch-detail-html-unavailable">
                    <div className="font-black text-[#8A2E00]">Rendered content unavailable</div>
                    <p className="mt-2 text-[#4A4A4A] text-[12px]">
                      {data.rendered.unavailable_reason}
                    </p>
                    <p className="mt-2 text-[11px] text-[#8A8A8A]">
                      Live re-render is deliberately disabled. Templates evolve over time —
                      re-rendering an old pitch from the current template would misrepresent
                      what the recipient saw.
                    </p>
                  </div>
                  {data.rendered.historical_template_preview_available && (
                    <div className="pt-2 space-y-2">
                      <button
                        onClick={loadHistoricalPreview}
                        disabled={loadingHistorical}
                        data-testid="hq-pitch-detail-view-historical-btn"
                        className="w-full px-3 py-2 rounded-full bg-[#0A0A0A] text-white text-[11px] font-black tracking-[0.16em] uppercase disabled:opacity-40 inline-flex items-center justify-center gap-2"
                      >
                        <FileText size={13}/> {loadingHistorical ? "Loading…" : "View historical template preview"}
                      </button>
                      <p className="text-[10px] text-[#8A8A8A] leading-relaxed">
                        Reference-only. Based on the current template — not the exact email received.
                      </p>
                      {historicalError && (
                        <div className="text-[11px] text-[#C1350B] font-bold"
                             data-testid="hq-pitch-detail-historical-error">
                          {historicalError}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>
        )}
        {showFollowUpGen && data && (
          <FollowUpGeneratorModal
            parent={data}
            onClose={() => setShowFollowUpGen(false)}
            onSent={() => setFollowUpRefreshKey((k) => k + 1)}
          />
        )}
      </div>
    </div>
  );
}
